
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";

interface GoogleSignInButtonProps {
  text?: string;
  className?: string;
}

export const GoogleSignInButton = ({ 
  text = "Sign in with Google", 
  className = "" 
}: GoogleSignInButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showBrandedDialog, setShowBrandedDialog] = useState(false);

  const openBrandedDialog = () => {
    setShowBrandedDialog(true);
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setShowBrandedDialog(false);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',  // This requests a refresh token for better session management
            prompt: 'select_account',  // Always show the account selector
          },
        },
      });

      if (error) {
        console.error("Error signing in with Google:", error);
        toast({
          variant: "destructive",
          title: "Authentication error",
          description: error.message || "There was a problem signing you in. Please try again.",
        });
        setIsLoading(false);
      }
      
      // The redirect will happen automatically, no need to navigate
    } catch (error) {
      console.error("Failed to sign in with Google:", error);
      toast({
        variant: "destructive",
        title: "Authentication error",
        description: "There was a problem signing you in. Please try again.",
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className={`w-full flex items-center justify-center gap-2 ${className}`}
        onClick={openBrandedDialog}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <svg
            className="h-5 w-5"
            aria-hidden="true"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
        )}
        <span className="font-serif">{text}</span>
      </Button>

      <Dialog open={showBrandedDialog} onOpenChange={setShowBrandedDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center font-title text-primary text-xl">
              NWSISD CEU Program Portal
            </DialogTitle>
            <DialogDescription className="text-center">
              You're about to sign in using your Google account
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex justify-center py-4">
            <img 
              src="/lovable-uploads/cc742b1a-3eb0-4c19-b31f-8e524aa87815.png"
              alt="NWSISD Logo"
              className="h-24 w-auto"
            />
          </div>
          
          <Card className="border-primary/20">
            <CardContent className="p-4">
              <p className="text-sm text-center font-serif">
                When you continue, you'll be redirected to Google to sign in securely.
                After signing in, you'll return to the CEU Program Portal.
              </p>
            </CardContent>
          </Card>

          <DialogFooter className="flex flex-col sm:flex-row sm:justify-center gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowBrandedDialog(false)}
              className="w-full sm:w-auto"
            >
              <span className="font-serif">Cancel</span>
            </Button>
            <Button 
              onClick={handleGoogleSignIn}
              className="w-full sm:w-auto flex items-center gap-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <svg
                  className="h-5 w-5"
                  aria-hidden="true"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
              )}
              <span className="font-serif">Continue with Google</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
